import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import { Subject, Subscription, throwError, timer } from 'rxjs';
import { retry } from 'rxjs/operators';
import { DocumentsConstants } from '../../../core/constants/documents.constants';
import { DocumentsMessagingConstants } from '../../../core/constants/messaging.constants';
import { DocumentUploadStatus } from '../../../core/enums/data.enums';
import { BulkDocumentUploadFileRequest, DocumentModel, DocumentUploadReservationModel } from '../../../core/models/data/document.model';
import { ServiceResultModel } from '../../../core/models/service/service-result.model';
import { DocumentService } from '../../../core/services/document.service';

/** One file in the upload queue. */
export interface UploadItem {
    tempId: number;
    // Absent for a non-uploadable entry surfaced as a failed row (e.g. a dropped folder).
    file?: File;
    fileName: string;
    sizeBytes: number;
    contentType: string;
    folderId: number;
    status: DocumentUploadStatus;
    percent: number;
    errorMessage?: string;
    // False for terminal rejections that can't be retried (e.g. a dropped folder). Real uploads omit
    // it (retryable by default).
    retryable?: boolean;
    reservationId?: number;
    resolvedName?: string;
    // In-memory only: the SAS URL the bytes are PUT to. Re-issued on retry; never persisted.
    uploadUrl?: string;
    document?: DocumentModel;
    sub?: Subscription;
}

/** Emitted when a file commits successfully, so the page can toast + refresh the file list. */
export interface UploadCompletedEvent {
    document: DocumentModel;
    originalName: string;
}

/**
 * Page-scoped manager for bulk direct-to-blob upload: a single all-or-nothing bulk `prepare` reserves
 * the whole batch (chunked for very large batches, every chunk prepared before any byte is uploaded so
 * exceeding the storage limit rejects the batch up front), then files PUT to their SAS URLs with a
 * bounded number in flight and each commits individually as it finishes. Oversized/empty files are
 * excluded before prepare and listed as failed. Failed files can be retried individually (which
 * re-prepares, so an expired SAS recovers). A new instance per page visit. Cancelling or leaving the
 * page force-expires any pending reservation so its reserved storage frees immediately; the server's
 * expiry job is the backstop for
 * cases we can't signal (e.g. tab close).
 */
@Injectable()
export class DocumentUploadManagerService implements OnDestroy {

    private documentService = inject(DocumentService);
    private nextId = 0;

    // tempIds reserved-but-not-yet-uploading, drained by `pump` as upload slots free up.
    private uploadQueue: number[] = [];

    public items = signal<UploadItem[]>([]);
    public hasActiveUploads = computed<boolean>(() => this.items().some(i => this.isActive(i)));

    public readonly completed = new Subject<UploadCompletedEvent>();
    // A batch-level rejection (prepare failed all-or-nothing) — the page surfaces this prominently.
    public readonly batchError = new Subject<string>();

    /**
     * Queue a batch of files for upload into the given folder. Oversized/empty files are excluded up
     * front (listed as failed); the rest are bulk-prepared together and uploaded concurrently.
     */
    public enqueue(files: File[], folderId: number): void {
        if (!files || files.length === 0) return;

        // Client-side pre-checks — exclude obvious rejections before prepare. Submitting an oversized
        // file would reject the whole batch (the API's bulk prepare is all-or-nothing), so these are
        // failed locally and never sent.
        const newItems: UploadItem[] = [];
        const valid: UploadItem[] = [];
        for (const file of files) {
            const item = this.buildItem(file, folderId);
            const localError = this.validateLocally(file);
            if (localError) {
                item.status = DocumentUploadStatus.Failed;
                item.errorMessage = localError;
                // Empty/oversized is a property of the file itself — retrying the same File can never
                // succeed, so don't offer a retry control for it.
                item.retryable = false;
            } else {
                valid.push(item);
            }
            newItems.push(item);
        }

        this.items.update(list => [...list, ...newItems]);

        if (valid.length > 0) {
            void this.prepareBatch(valid, folderId);
        }
    }

    /**
     * Surface a dropped folder as a failed entry in the dock. Folder upload isn't supported in v1
     * (nested-structure preservation is a future capability), so nothing is uploaded — the entry is
     * terminal and not retryable, and the user clears it by dismissing the dock.
     */
    public rejectFolder(folderName: string): void {
        this.items.update(list => [...list, {
            tempId: this.nextId++,
            fileName: folderName,
            sizeBytes: 0,
            contentType: '',
            folderId: 0,
            status: DocumentUploadStatus.Failed,
            percent: 0,
            retryable: false,
            errorMessage: DocumentsMessagingConstants.DOCUMENTS_UPLOAD_FOLDER_NOT_SUPPORTED()
        }]);
    }

    private buildItem(file: File, folderId: number): UploadItem {
        return {
            tempId: this.nextId++,
            file,
            fileName: file.name,
            sizeBytes: file.size,
            contentType: file.type || DocumentsConstants.DEFAULT_CONTENT_TYPE(),
            folderId,
            status: DocumentUploadStatus.Pending,
            percent: 0
        };
    }

    /** Mirror the API's basic upload guards client-side so obvious rejections never hit the network. */
    private validateLocally(file: File): string | undefined {
        if (file.size === 0) {
            return DocumentsMessagingConstants.DOCUMENTS_UPLOAD_EMPTY_FILE();
        }
        if (file.size > DocumentsConstants.MAX_FILE_SIZE_BYTES()) {
            return DocumentsMessagingConstants.DOCUMENTS_UPLOAD_TOO_LARGE().replaceValuesWithObject({
                limit: DocumentsConstants.MAX_FILE_SIZE_LABEL()
            });
        }
        return undefined;
    }

    public cancel(tempId: number): void {
        this.teardown(this.find(tempId));
        this.uploadQueue = this.uploadQueue.filter(id => id !== tempId);
        this.items.update(list => list.filter(i => i.tempId !== tempId));
        // Cancelling an in-flight or queued upload frees a slot — let the next queued file start.
        this.pump();
    }

    /** Abort and clear every queued/in-flight upload — e.g. the user leaves the page mid-upload. */
    public cancelAll(): void {
        this.items().forEach(item => this.teardown(item));
        this.uploadQueue = [];
        this.items.set([]);
    }

    // Stop an item's in-flight PUT and free its uncommitted reservation.
    private teardown(item?: UploadItem): void {
        item?.sub?.unsubscribe();
        this.releaseReservation(item);
    }

    // Free an item's reserved-but-uncommitted upload immediately so its reserved storage isn't held
    // until the server's expiry job runs. A committed (Done) upload keeps its space.
    private releaseReservation(item?: UploadItem): void {
        if (item?.status === DocumentUploadStatus.Done) return;
        this.expireReservation(item?.reservationId);
    }

    // Best-effort: ask the server to release a pending reservation now rather than at its expiry job.
    // A 404/409 (already gone or committed) isn't actionable, so the result is ignored.
    private expireReservation(reservationId?: number): void {
        if (!reservationId) return;
        void this.documentService.expireUploadReservation(reservationId);
    }

    /** Page-scoped: when the documents page is destroyed (navigation away), stop any active upload. */
    public ngOnDestroy(): void {
        this.cancelAll();
    }

    /** Clear terminal (Done/Failed) items from the dock, keeping only those still in flight. */
    public dismissCompleted(): void {
        this.items.update(list => list.filter(i => this.isActive(i)));
    }

    // An upload still in flight (queued or uploading) — i.e. not yet in a terminal Done/Failed state.
    private isActive(item: UploadItem): boolean {
        return item.status === DocumentUploadStatus.Pending || item.status === DocumentUploadStatus.Uploading;
    }

    /**
     * Retry a single failed file. Re-runs the full single-file flow (prepare → PUT → commit) so an
     * expired SAS is re-issued and the per-file/storage-limit checks are re-applied to just this file.
     */
    public retry(tempId: number): void {
        const item = this.find(tempId);
        if (!item || item.status !== DocumentUploadStatus.Failed) return;
        // Non-retryable failures can't succeed on retry: a dropped folder, or a file excluded locally
        // as empty/oversized (a property of the file itself). Both are flagged retryable: false.
        if (item.retryable === false || !item.file) return;

        this.patch(tempId, {
            status: DocumentUploadStatus.Pending,
            percent: 0,
            errorMessage: undefined,
            reservationId: undefined,
            resolvedName: undefined,
            uploadUrl: undefined
        });
        void this.prepareSingle(tempId);
    }

    //#region Prepare

    /**
     * Bulk-prepare the whole valid batch before any upload starts. Chunked for very large batches, but
     * every chunk is prepared up front so the all-or-nothing storage-limit check rejects the batch before a
     * single byte is uploaded. On any chunk failure, reservations created by earlier chunks are
     * expired and every file in the batch is failed.
     */
    private async prepareBatch(items: UploadItem[], folderId: number): Promise<void> {
        const chunkSize = DocumentsConstants.PREPARE_CHUNK_SIZE();
        const createdReservationIds: number[] = [];
        const reserved: { tempId: number; reservation: DocumentUploadReservationModel }[] = [];

        for (let offset = 0; offset < items.length; offset += chunkSize) {
            const chunk = items.slice(offset, offset + chunkSize);
            const files: BulkDocumentUploadFileRequest[] = chunk.map(i => ({
                name: i.fileName,
                contentType: i.contentType,
                declaredSizeBytes: i.sizeBytes
            }));

            const result = await this.documentService.bulkPrepareUpload({ folderId, files });

            // All-or-nothing: a short/failed chunk rolls back any reservations already created and
            // fails the entire batch — partial reservations must never be left dangling.
            if (!result.success || !result.data || result.data.length !== chunk.length) {
                createdReservationIds.forEach(id => this.expireReservation(id));
                const message = this.batchFailureMessage(result);
                items.forEach(i => this.fail(i.tempId, message));
                this.batchError.next(message);
                return;
            }

            result.data.forEach((reservation, index) => {
                createdReservationIds.push(reservation.reservationId);
                reserved.push({ tempId: chunk[index].tempId, reservation });
            });
        }

        reserved.forEach(({ tempId, reservation }) => this.queuePreparedUpload(tempId, reservation));
        this.pump();
    }

    /** Single-file prepare path used by retry. */
    private async prepareSingle(tempId: number): Promise<void> {
        const item = this.find(tempId);
        if (!item) return;

        const prep = await this.documentService.prepareUpload({
            folderId: item.folderId,
            name: item.fileName,
            contentType: item.contentType,
            declaredSizeBytes: item.sizeBytes
        });
        if (!prep.success || !prep.data) {
            this.fail(tempId, prep.message ?? this.genericFailureMessage());
            return;
        }
        this.queuePreparedUpload(tempId, prep.data);
        this.pump();
    }

    /**
     * Attach a reservation's handles to its item and queue the upload. If the item is gone — the user
     * cancelled the row or left the page while prepare was in flight — expire the orphaned reservation
     * instead of queueing a ghost tempId that would leak it until the server's expiry job.
     */
    private queuePreparedUpload(tempId: number, reservation: DocumentUploadReservationModel): void {
        if (!this.find(tempId)) {
            this.expireReservation(reservation.reservationId);
            return;
        }
        this.patch(tempId, {
            reservationId: reservation.reservationId,
            resolvedName: reservation.resolvedName,
            uploadUrl: reservation.uploadUrl
        });
        this.uploadQueue.push(tempId);
    }

    /** Batch-rejection copy: exceeding the storage limit gets the limit-specific message; otherwise the API's. */
    private batchFailureMessage(result: ServiceResultModel<DocumentUploadReservationModel[]>): string {
        // Match on 'storage limit' — that's the wording the API throws ("...would exceed the
        // organization's storage limit."). Keep this matcher in sync with the API message text.
        if (result.statusCode === 422 && (result.message ?? '').toLowerCase().includes('storage limit')) {
            return DocumentsMessagingConstants.DOCUMENTS_UPLOAD_BATCH_LIMIT_EXCEEDED();
        }
        return result.message ?? DocumentsMessagingConstants.DOCUMENTS_UPLOAD_BATCH_REJECTED();
    }

    //#endregion Prepare

    //#region Upload pipeline

    /**
     * Start as many queued uploads as there are free slots (the AC calls for 4-6 files in flight). A
     * slot is occupied from the moment a file starts uploading until it commits or fails, so the count
     * is read from live item state rather than a separate counter.
     */
    private pump(): void {
        let slots = DocumentsConstants.UPLOAD_CONCURRENCY()
            - this.items().filter(i => i.status === DocumentUploadStatus.Uploading).length;

        while (slots > 0 && this.uploadQueue.length > 0) {
            const tempId = this.uploadQueue.shift()!;
            const item = this.find(tempId);
            // Skip files cancelled/removed while queued (or otherwise no longer pending).
            if (!item || item.status !== DocumentUploadStatus.Pending || !item.uploadUrl) continue;
            slots--;
            this.startUpload(tempId);
        }
    }

    /** PUT the bytes to the reserved SAS URL, retrying transient failures, then commit on completion. */
    private startUpload(tempId: number): void {
        const item = this.find(tempId);
        if (!item?.uploadUrl || !item.file) return;

        this.patch(tempId, { status: DocumentUploadStatus.Uploading, percent: 0, errorMessage: undefined });

        const sub = this.documentService
            .uploadFileToBlob(item.uploadUrl, item.file, item.contentType)
            .pipe(
                retry({
                    count: DocumentsConstants.UPLOAD_MAX_RETRIES(),
                    delay: (error, retryCount) => this.isTransient(error)
                        ? timer(DocumentsConstants.UPLOAD_RETRY_BASE_DELAY_MS() * retryCount)
                        : throwError(() => error)
                })
            )
            .subscribe({
                next: e => { if (!e.done) this.patch(tempId, { percent: e.progress }); },
                error: (err: HttpErrorResponse) => this.fail(tempId, this.mapBlobError(err)),
                complete: () => { this.patch(tempId, { percent: 100 }); void this.commit(tempId); }
            });
        this.patch(tempId, { sub });
    }

    private async commit(tempId: number): Promise<void> {
        const item = this.find(tempId);
        if (!item?.reservationId) {
            this.pump();
            return;
        }

        const result = await this.documentService.commitUpload({ reservationId: item.reservationId });
        if (!result.success || !result.data) {
            this.fail(tempId, result.message ?? this.genericFailureMessage());
            return;
        }
        this.patch(tempId, { status: DocumentUploadStatus.Done, percent: 100, document: result.data });
        this.completed.next({ document: result.data, originalName: item.fileName });
        // This file's slot is now free — start the next queued upload.
        this.pump();
    }

    //#endregion Upload pipeline

    private isTransient(error: unknown): boolean {
        return error instanceof HttpErrorResponse && (error.status === 0 || error.status >= 500);
    }

    private mapBlobError(error: HttpErrorResponse): string {
        // An expired SAS surfaces as a 403 from Azure; retry re-prepares, so the user can recover.
        if (error?.status === 403) {
            return DocumentsMessagingConstants.DOCUMENTS_UPLOAD_SAS_EXPIRED();
        }
        return DocumentsMessagingConstants.DOCUMENTS_UPLOAD_FAILED().replaceValuesWithObject({
            reason: error?.statusText ?? ''
        });
    }

    /** "Upload failed. Please try again." — used when prepare/commit fails without a specific message. */
    private genericFailureMessage(): string {
        return DocumentsMessagingConstants.DOCUMENTS_UPLOAD_FAILED().replaceValuesWithObject({
            reason: DocumentsMessagingConstants.DOCUMENTS_UPLOAD_GENERIC_REASON()
        });
    }

    private fail(tempId: number, message: string): void {
        // Abandon any reservation the item still holds so its reserved storage frees now and a later retry doesn't
        // strand it. No-op for items that failed before getting a reservation (local pre-checks; the
        // batch-prepare failure path rolls back its own reservations).
        this.releaseReservation(this.find(tempId));
        this.patch(tempId, {
            status: DocumentUploadStatus.Failed,
            errorMessage: message,
            reservationId: undefined,
            uploadUrl: undefined
        });
        // A failed file frees its slot — let the next queued upload start.
        this.pump();
    }

    private find(tempId: number): UploadItem | undefined {
        return this.items().find(i => i.tempId === tempId);
    }

    private patch(tempId: number, changes: Partial<UploadItem>): void {
        this.items.update(list => list.map(i => i.tempId === tempId ? { ...i, ...changes } : i));
    }
}
